import React, { useEffect, useState, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Plus, Minus, Trash2, ScanLine, Printer, X, Download, MessageCircle, Edit } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import Receipt from "../components/Receipt";
import AddProductDialog from "../components/AddProductDialog";
import SessionBar from "../components/SessionBar";
import { useSession } from "../contexts/SessionContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";

const PAYMENT_METHODS = [
  { id: "cash", label: "Cash", color: "bg-moss" },
  { id: "gpay", label: "GPay", color: "bg-terracotta" },
  { id: "mpay", label: "MPay", color: "bg-ochre" },
  { id: "card", label: "Card", color: "bg-ink" },
];

export default function POS() {
  const { activeShop, activeShopId } = useShop();
  const { session: posSession } = useSession();
  const [products, setProducts] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [custFocus, setCustFocus] = useState(null); // 'name' | 'phone' | null
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]); // {product_id, name, mrp, rate, qty}
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [printBill, setPrintBill] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [prefillBarcode, setPrefillBarcode] = useState("");
  const [loyalty, setLoyalty] = useState(null); // {balance, min_redeem, can_redeem}
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemPts, setRedeemPts] = useState(0);
  const [redeemBill, setRedeemBill] = useState(null);
  // Multi-bill tabs (Odoo-style)
  const genOrderId = () => `O${Date.now().toString().slice(-5)}`;
  const [orders, setOrders] = useState(() => [{ id: genOrderId(), cart: [], customer: { name: "", phone: "", address: "" }, discount: 0 }]);
  const [activeIdx, setActiveIdx] = useState(0);
  const barcodeRef = useRef(null);
  const cartScrollRef = useRef(null);
  const lastCartLen = useRef(0);

  // Snapshot current state into active order slot on every change
  useEffect(() => {
    setOrders((os) => {
      const next = [...os];
      if (next[activeIdx]) next[activeIdx] = { ...next[activeIdx], cart, customer, discount };
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, customer, discount]);

  const switchOrder = (idx) => {
    if (idx === activeIdx) return;
    const target = orders[idx];
    if (!target) return;
    setActiveIdx(idx);
    setCart(target.cart || []);
    setCustomer(target.customer || { name: "", phone: "", address: "" });
    setDiscount(target.discount || 0);
  };

  const addOrder = () => {
    // Save current into active first (already in effect), then push new empty order
    const newOrd = { id: genOrderId(), cart: [], customer: { name: "", phone: "", address: "" }, discount: 0 };
    setOrders((os) => {
      const snap = [...os];
      if (snap[activeIdx]) snap[activeIdx] = { ...snap[activeIdx], cart, customer, discount };
      return [...snap, newOrd];
    });
    setActiveIdx(orders.length);
    setCart([]); setCustomer({ name: "", phone: "", address: "" }); setDiscount(0);
  };

  const closeOrder = (idx) => {
    if (orders.length === 1) {
      // Reset the only order instead of removing
      setCart([]); setCustomer({ name: "", phone: "", address: "" }); setDiscount(0);
      return;
    }
    const remaining = orders.filter((_, i) => i !== idx);
    const newActive = idx <= activeIdx ? Math.max(0, activeIdx - 1) : activeIdx;
    setOrders(remaining);
    setActiveIdx(newActive);
    const target = remaining[newActive];
    setCart(target.cart || []);
    setCustomer(target.customer || { name: "", phone: "", address: "" });
    setDiscount(target.discount || 0);
  };

  // Auto-scroll to bottom when item added
  React.useEffect(() => {
    if (cart.length > lastCartLen.current && cartScrollRef.current) {
      cartScrollRef.current.scrollTop = cartScrollRef.current.scrollHeight;
    }
    lastCartLen.current = cart.length;
  }, [cart]);

  // Fetch loyalty when phone changes
  React.useEffect(() => {
    const phone = (customer.phone || "").replace(/\D/g, "");
    if (phone.length === 10) {
      api.get(`/customers/${phone}/loyalty`).then((r) => setLoyalty(r.data)).catch(() => setLoyalty(null));
    } else {
      setLoyalty(null); setRedeemPts(0);
    }
  }, [customer.phone]);

  const refreshLoyalty = async () => {
    const phone = (customer.phone || "").replace(/\D/g, "");
    if (phone.length === 10) {
      try { const r = await api.get(`/customers/${phone}/loyalty`); setLoyalty(r.data); } catch (_) {}
    }
  };

  const handleRedeem = async () => {
    const phone = (customer.phone || "").replace(/\D/g, "");
    if (phone.length !== 10) { toast.error("Enter valid 10-digit customer phone"); return; }
    const pts = Number(redeemPts || 0);
    const minR = loyalty?.min_redeem || 300;
    if (pts < minR) { toast.error(`Minimum ${minR} points required to redeem`); return; }
    if (pts > (loyalty?.balance || 0)) { toast.error(`Insufficient balance (${loyalty?.balance || 0} pts)`); return; }
    if (!posSession) { toast.error("Please open a session first"); return; }
    try {
      const res = await api.post("/loyalty/redeem", {
        shop_id: activeShopId,
        customer_phone: phone,
        customer_name: customer.name,
        points: pts,
      });
      toast.success(`Redeemed ${pts} pts (₹${pts}) — Bill ${res.data.bill_no}`);
      setRedeemOpen(false);
      setRedeemBill(res.data);
      setPrintBill(res.data); // print the redemption receipt
      setRedeemPts(0);
      refreshLoyalty();
      // Auto-send SMS with LP balance info
      if (phone.length === 10) {
        api.post(`/bills/${res.data.bill_id}/send-sms`, { phone }).catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed");
    }
  };

  const load = async () => {
    const r = await api.get("/products");
    setProducts(r.data);
  };
  useEffect(() => { load(); }, []);

  // Load customers once for autocomplete
  useEffect(() => {
    api.get("/customers").then((r) => setCustomerList(r.data || [])).catch(() => {});
  }, []);

  // Customer autocomplete suggestions (matches by name OR phone)
  const customerSuggestions = useMemo(() => {
    const q = (custFocus === "name" ? customer.name : customer.phone || "").trim().toLowerCase();
    if (q.length < 2) return [];
    return customerList.filter((c) =>
      (c.name && c.name.toLowerCase().includes(q)) || (c.phone && c.phone.includes(q))
    ).slice(0, 6);
  }, [custFocus, customer.name, customer.phone, customerList]);

  const pickCustomer = (c) => {
    setCustomer({ name: c.name || "", phone: c.phone || "", address: c.address || "" });
    setCustFocus(null);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Apply per-shop category filter (if shop has allowed_categories set)
    const allowed = activeShop?.allowed_categories || [];
    let base = products;
    if (allowed.length > 0) {
      const allowedLower = allowed.map((c) => c.toLowerCase());
      base = products.filter((p) => allowedLower.includes((p.category || "").toLowerCase()));
    }
    if (!q) return base.slice(0, 60);
    return base.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      p.barcode?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q)
    ).slice(0, 60);
  }, [search, products, activeShop]);

  const addToCart = (p) => {
    const stock = p.stock?.[activeShopId] || 0;
    const exp = p.expiry?.[activeShopId];
    if (exp && new Date(exp) < new Date()) {
      toast.error(`${p.name} is EXPIRED. Cannot sell.`);
      return;
    }
    if (stock <= 0) {
      toast.error(`${p.name} out of stock at ${activeShopId}`);
      return;
    }
    setCart((c) => {
      const idx = c.findIndex((x) => x.product_id === p.product_id);
      if (idx >= 0) {
        if (c[idx].qty + 1 > stock) {
          toast.error(`Only ${stock} in stock`);
          return c;
        }
        const n = [...c]; n[idx] = { ...n[idx], qty: n[idx].qty + 1 }; return n;
      }
      return [...c, { product_id: p.product_id, name: p.name, mrp: Number(p.mrp) || 0, rate: Number(p.retail_price) || 0, qty: 1, max: stock }];
    });
  };

  const handleBarcode = (e) => {
    if (e.key === "Enter") {
      const v = e.target.value.trim();
      if (!v) return;
      const p = products.find((x) => x.barcode === v);
      if (p) { addToCart(p); e.target.value = ""; }
      else {
        toast.error(`Barcode ${v} not found - add new product`);
        setPrefillBarcode(v);
        setAddOpen(true);
        e.target.value = "";
      }
    }
  };

  const updateQty = (idx, delta) => setCart((c) => {
    const n = [...c]; const nq = n[idx].qty + delta;
    if (nq <= 0) return n.filter((_, i) => i !== idx);
    if (n[idx].max && nq > n[idx].max) { toast.error(`Max ${n[idx].max}`); return c; }
    n[idx] = { ...n[idx], qty: nq }; return n;
  });
  const updateRate = (idx, val) => setCart((c) => {
    const n = [...c]; n[idx] = { ...n[idx], rate: Number(val) || 0 }; return n;
  });
  const updateMrp = (idx, val) => setCart((c) => {
    const n = [...c]; n[idx] = { ...n[idx], mrp: Number(val) || 0 }; return n;
  });
  const remove = (idx) => setCart((c) => c.filter((_, i) => i !== idx));

  const subtotal = cart.reduce((s, x) => s + x.rate * x.qty, 0);
  const mrpTotal = cart.reduce((s, x) => s + x.mrp * x.qty, 0);
  const savings = Math.max(0, mrpTotal - subtotal);
  const netBefore = subtotal - Number(discount || 0);
  const netTotal = Math.max(0, Math.round(netBefore));

  const checkout = async (paymentsArr) => {
    if (cart.length === 0) return;
    if (!posSession) {
      toast.error("Please open a session first");
      setPaymentOpen(false);
      return;
    }
    try {
      const res = await api.post("/bills", {
        shop_id: activeShopId,
        items: cart.map((x) => ({
          product_id: x.product_id, name: x.name, mrp: x.mrp, rate: x.rate, qty: x.qty
        })),
        customer_name: customer.name,
        customer_phone: customer.phone,
        customer_address: customer.address,
        payments: paymentsArr,
        payment_method: paymentsArr[0].method,
        discount: Number(discount || 0),
      });
      setPaymentOpen(false);
      setPrintBill(res.data);
      setCart([]);
      setCustomer({ name: "", phone: "", address: "" });
      setDiscount(0);
      // Remove this order tab if more than one tab exists
      if (orders.length > 1) {
        const remaining = orders.filter((_, i) => i !== activeIdx);
        const newActive = Math.max(0, activeIdx - (activeIdx === orders.length - 1 ? 1 : 0));
        setOrders(remaining.map((o, i) => i === Math.min(newActive, remaining.length - 1) ? o : o));
        setActiveIdx(Math.min(newActive, remaining.length - 1));
        const target = remaining[Math.min(newActive, remaining.length - 1)];
        if (target) {
          setCart(target.cart || []);
          setCustomer(target.customer || { name: "", phone: "", address: "" });
          setDiscount(target.discount || 0);
        }
      }
      load();
      toast.success(`Bill ${res.data.bill_no} · LP +${res.data.points_earned}${res.data.points_redeemed ? ` -${res.data.points_redeemed}` : ''}`);
      // Auto-send SMS if phone provided
      const phoneDigits = (res.data.customer_phone || "").replace(/\D/g, "");
      if (phoneDigits.length === 10) {
        api.post(`/bills/${res.data.bill_id}/send-sms`, { phone: phoneDigits }).catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  return (
    <div className="h-screen grid grid-cols-1 md:grid-cols-12 overflow-hidden" data-testid="pos-page">
      {/* Left: Products */}
      <div className="md:col-span-8 bg-sand flex flex-col overflow-hidden">
        <SessionBar />
        {/* Order tabs — Odoo style */}
        <div className="px-4 pt-2 bg-white border-b border-border flex items-center gap-1 overflow-x-auto scroll-thin" data-testid="order-tabs">
          {orders.map((o, i) => {
            const isActive = i === activeIdx;
            const itemCount = (o.cart || []).reduce((s, x) => s + x.qty, 0);
            return (
              <div
                key={o.id}
                onClick={() => switchOrder(i)}
                data-testid={`order-tab-${i}`}
                className={`group flex items-center gap-2 px-3 py-1.5 rounded-t border-t border-l border-r cursor-pointer text-xs transition-colors ${
                  isActive ? "bg-sand border-terracotta text-terracotta font-bold" : "bg-surface-alt border-border hover:bg-sand/60"
                }`}
              >
                <span className="font-mono">{o.id}</span>
                {itemCount > 0 && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${isActive ? "bg-terracotta text-white" : "bg-muted-foreground/20"}`}>{itemCount}</span>
                )}
                {orders.length > 1 && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); closeOrder(i); }}
                    className="opacity-40 hover:opacity-100 hover:text-destructive"
                    data-testid={`order-close-${i}`}
                  >
                    <X size={12} />
                  </span>
                )}
              </div>
            );
          })}
          <button
            onClick={addOrder}
            data-testid="order-add-btn"
            className="ml-1 flex items-center justify-center w-7 h-7 rounded-full bg-terracotta text-white hover:bg-terracotta-hover mb-1"
            title="New order"
          >
            <Plus size={14} />
          </button>
        </div>
        <div className="p-4 border-b border-border bg-white flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="pos-search"
              placeholder="Search products by name / category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-11"
            />
          </div>
          <div className="relative w-72">
            <ScanLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-terracotta" />
            <Input
              data-testid="pos-barcode"
              ref={barcodeRef}
              placeholder="Scan barcode (or paste & Enter)"
              onKeyDown={handleBarcode}
              className="pl-9 h-11"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((p) => {
              const stock = p.stock?.[activeShopId] || 0;
              const exp = p.expiry?.[activeShopId];
              const expired = exp && new Date(exp) < new Date();
              return (
                <button
                  key={p.product_id}
                  onClick={() => addToCart(p)}
                  data-testid={`pos-product-${p.product_id}`}
                  className={`relative text-left bg-white border border-border rounded-md overflow-hidden hover:border-terracotta hover:bg-terracotta/5 transition-colors duration-200 ${expired ? "opacity-50" : ""}`}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(ev) => { ev.stopPropagation(); setEditProduct(p); }}
                    data-testid={`pos-edit-${p.product_id}`}
                    className="absolute top-1 right-1 z-10 bg-white border border-border rounded p-1 shadow-sm opacity-0 group-hover:opacity-100 hover:bg-terracotta hover:text-white hover:border-terracotta transition-all"
                    style={{ opacity: 1 }}
                  >
                    <Edit size={12} />
                  </span>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-24 object-cover bg-sand" />
                  ) : (
                    <div className="w-full h-24 bg-gradient-to-br from-sand to-surface-alt flex items-center justify-center">
                      <span className="font-heading text-3xl text-terracotta/30 font-bold">{p.name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{p.category}</div>
                    <div className="font-heading font-semibold text-ink mt-1 line-clamp-2 leading-tight text-sm">{p.name}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="font-mono text-terracotta font-bold">₹{p.retail_price}</div>
                      <div className="text-xs text-muted-foreground">{stock} left</div>
                    </div>
                    {expired && <div className="text-[10px] text-destructive mt-1 font-bold">EXPIRED</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: Cart */}
      <div className="md:col-span-4 bg-white border-l border-border flex flex-col overflow-hidden">
        <div className="p-4 border-b border-border relative">
          <div className="label-overline text-muted-foreground">Customer (optional)</div>
          <Input
            data-testid="customer-name"
            placeholder="Name (type 2+ chars to search)"
            value={customer.name}
            onChange={(e)=>setCustomer({...customer, name:e.target.value})}
            onFocus={() => setCustFocus("name")}
            onBlur={() => setTimeout(() => setCustFocus(null), 150)}
            className="mt-2"
          />
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Input
              data-testid="customer-phone"
              placeholder="Phone"
              value={customer.phone}
              onChange={(e)=>setCustomer({...customer, phone:e.target.value})}
              onFocus={() => setCustFocus("phone")}
              onBlur={() => setTimeout(() => setCustFocus(null), 150)}
            />
            <Input data-testid="customer-address" placeholder="Address" value={customer.address} onChange={(e)=>setCustomer({...customer, address:e.target.value})} />
          </div>
          {custFocus && customerSuggestions.length > 0 && (
            <div className="absolute left-4 right-4 top-full mt-1 bg-white border border-border rounded shadow-lg z-30 max-h-60 overflow-y-auto" data-testid="customer-suggestions">
              {customerSuggestions.map((c) => (
                <button
                  key={c.phone}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                  className="w-full text-left px-3 py-2 hover:bg-sand border-b border-border last:border-0 flex items-center justify-between gap-2"
                  data-testid={`customer-sugg-${c.phone}`}
                >
                  <div>
                    <div className="font-medium text-sm">{c.name || "—"}</div>
                    <div className="font-mono text-xs text-muted-foreground">{c.phone}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-moss font-bold">LP {c.lp_balance || 0}</div>
                    <div className="text-[10px] text-muted-foreground">{c.bill_count || 0} bills</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin" ref={cartScrollRef}>
          {cart.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">Click products on the left or scan barcode to add to cart.</div>
          )}
          {cart.map((it, idx) => (
            <div key={idx} className="p-3 border-b border-border" data-testid={`cart-row-${idx}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-medium text-sm">{it.name}</div>
                  <div className="flex items-center gap-1 mt-1 text-[10px]">
                    <span className="text-muted-foreground">MRP ₹</span>
                    <Input
                      type="number"
                      value={Number(it.mrp) || 0}
                      onChange={(e) => updateMrp(idx, e.target.value)}
                      className="w-16 h-6 text-right font-mono text-[10px] px-1"
                      data-testid={`cart-mrp-${idx}`}
                    />
                    {Number(it.mrp) > Number(it.rate) && (
                      <span className="ml-2 text-moss font-semibold">
                        Save ₹{Math.round((it.mrp - it.rate) * it.qty)}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => remove(idx)} className="text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${idx}`}><Trash2 size={14} /></button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={()=>updateQty(idx,-1)} data-testid={`cart-minus-${idx}`}><Minus size={12} /></Button>
                  <Input
                    type="number"
                    min="1"
                    value={it.qty}
                    onChange={(e) => {
                      const n = Math.max(1, Number(e.target.value) || 1);
                      setCart((c) => {
                        const next = [...c];
                        if (next[idx].max && n > next[idx].max) {
                          toast.error(`Max ${next[idx].max}`);
                          return c;
                        }
                        next[idx] = { ...next[idx], qty: n };
                        return next;
                      });
                    }}
                    className="w-14 h-7 text-center font-mono text-sm px-1"
                    data-testid={`cart-qty-${idx}`}
                  />
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={()=>updateQty(idx,+1)} data-testid={`cart-plus-${idx}`}><Plus size={12} /></Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">Rate</span>
                  <span className="text-xs text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    value={it.rate}
                    onChange={(e)=>updateRate(idx, e.target.value)}
                    className="w-20 h-7 text-right font-mono text-sm"
                    data-testid={`cart-rate-${idx}`}
                  />
                  <span className="font-mono font-semibold w-16 text-right">₹{Math.round(it.rate*it.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-border p-4 bg-sand">
          <div className="flex justify-between text-sm"><span>Subtotal</span><span className="font-mono">₹{Math.round(subtotal)}</span></div>
          <div className="flex justify-between text-sm"><span>MRP Total</span><span className="font-mono text-muted-foreground line-through">₹{Math.round(mrpTotal)}</span></div>
          <div className="flex justify-between text-sm text-moss"><span>You Save</span><span className="font-mono font-bold">₹{Math.round(savings)}</span></div>
          <div className="flex justify-between text-sm items-center mt-1">
            <span>Discount</span>
            <Input type="number" value={discount} onChange={(e)=>setDiscount(e.target.value)} className="w-24 h-7 text-right font-mono" data-testid="cart-discount" />
          </div>
          <div className="flex justify-between font-heading text-2xl font-bold mt-3 border-t border-border pt-3">
            <span>Net Total</span>
            <span className="font-mono text-terracotta" data-testid="cart-net-total">₹{Math.round(netTotal)}</span>
          </div>
          {loyalty && loyalty.balance > 0 && (
            <div className="bg-moss/10 border border-moss/30 rounded p-2 mt-2 text-xs" data-testid="loyalty-section">
              <div className="flex justify-between">
                <span className="font-bold text-moss">LP Balance</span>
                <span className="font-mono font-bold text-moss" data-testid="loyalty-balance">{loyalty.balance} LP (₹{loyalty.balance})</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="text-muted-foreground text-[10px]">
                  {loyalty.can_redeem
                    ? `Min ${loyalty.min_redeem || 300} LP to withdraw · separate bill`
                    : `Need ${(loyalty.min_redeem || 300) - loyalty.balance} more LP (min ${loyalty.min_redeem || 300})`}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!loyalty.can_redeem}
                  className="h-7 text-[10px] bg-moss text-white hover:bg-moss/90 disabled:opacity-50"
                  onClick={() => { setRedeemPts(loyalty.min_redeem || 300); setRedeemOpen(true); }}
                  data-testid="redeem-loyalty-btn"
                >
                  Withdraw LP
                </Button>
              </div>
            </div>
          )}
          <Button
            disabled={cart.length === 0}
            onClick={() => setPaymentOpen(true)}
            data-testid="cart-checkout-btn"
            className="w-full mt-3 bg-terracotta hover:bg-terracotta-hover text-white h-12 text-base font-semibold"
          >
            Checkout · ₹{Math.round(netTotal)}
          </Button>
        </div>
      </div>

      {/* Payment Modal - supports split */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Payment — ₹{Math.round(netTotal)}</DialogTitle></DialogHeader>
          <PaymentBody netTotal={netTotal} onPay={checkout} loyalty={loyalty} customerPhone={customer.phone} />
        </DialogContent>
      </Dialog>

      {/* Loyalty Redeem Modal */}
      <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
        <DialogContent data-testid="redeem-dialog">
          <DialogHeader><DialogTitle className="font-heading">Redeem Loyalty Points</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-moss/5 border border-moss/30 rounded p-3">
              <div className="flex justify-between"><span>Customer</span><span className="font-bold">{customer.name || "—"} · {customer.phone}</span></div>
              <div className="flex justify-between mt-1"><span>Available</span><span className="font-mono font-bold text-moss">{loyalty?.balance || 0} pts (₹{loyalty?.balance || 0})</span></div>
              <div className="flex justify-between mt-1 text-xs text-muted-foreground"><span>Min redeem</span><span>{loyalty?.min_redeem || 300} pts</span></div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Points to redeem:</span>
              <Input
                type="number"
                min={loyalty?.min_redeem || 300}
                max={loyalty?.balance || 0}
                value={redeemPts}
                onChange={(e) => setRedeemPts(Number(e.target.value) || 0)}
                className="w-32 font-mono"
                data-testid="redeem-pts-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRedeemPts(loyalty?.balance || 0)}
                data-testid="redeem-max-btn"
              >MAX</Button>
            </div>
            <div className="text-xs text-muted-foreground">
              Yeh ek <b>alag bill</b> generate karega (LOY-...). Customer ko {redeemPts} pts ke against ₹{redeemPts} cash/credit milega.
            </div>
            <Button
              className="w-full bg-moss hover:bg-moss/90 text-white"
              onClick={handleRedeem}
              data-testid="redeem-confirm-btn"
            >
              Confirm Redemption · ₹{redeemPts}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Product (barcode-not-found) */}
      <AddProductDialog
        open={addOpen}
        onOpenChange={(o) => { setAddOpen(o); if (!o) setPrefillBarcode(""); }}
        onCreated={load}
        prefillBarcode={prefillBarcode}
      />

      {/* Quick Edit Product (from POS card) */}
      <AddProductDialog
        open={!!editProduct}
        onOpenChange={(o) => { if (!o) setEditProduct(null); }}
        onCreated={load}
        product={editProduct}
      />

      {/* Print Receipt Modal */}
      <Dialog open={!!printBill} onOpenChange={(o) => !o && setPrintBill(null)}>
        <DialogContent className="max-w-md p-0 bg-transparent shadow-none border-0" data-testid="print-dialog">
          <div className="bg-white rounded-lg p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3 no-print flex-wrap gap-2">
              <div className="font-heading font-bold">Bill Preview</div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={() => printReceipt(printBill, activeShop)} data-testid="print-btn" className="bg-terracotta text-white hover:bg-terracotta-hover gap-1"><Printer size={14}/>Print</Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(printBill)} data-testid="pdf-btn" className="gap-1"><Download size={14}/>PDF</Button>
                <Button size="sm" variant="outline" onClick={() => sendViaSms(printBill, "sms")} data-testid="sms-btn" className="gap-1 border-ochre text-ochre hover:bg-ochre/10"><MessageCircle size={14}/>SMS</Button>
                <Button size="sm" variant="outline" onClick={() => sendViaSms(printBill, "whatsapp")} data-testid="whatsapp-btn" className="gap-1 border-moss text-moss hover:bg-moss/10"><MessageCircle size={14}/>WhatsApp</Button>
                <Button size="sm" variant="outline" onClick={() => setPrintBill(null)} data-testid="close-print"><X size={14}/></Button>
              </div>
            </div>
            <div id="receipt-preview-container">
              <Receipt bill={printBill} shop={activeShop} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Download bill as PDF (thermal 80mm width)
async function downloadPdf(bill) {
  const node = document.getElementById("receipt-preview-container")?.querySelector(".printable");
  if (!node) {
    alert("Preview not found");
    return;
  }
  try {
    // Wait for any pending paint
    await new Promise((r) => setTimeout(r, 100));
    const canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      allowTaint: true,
      logging: false,
    });
    const imgData = canvas.toDataURL("image/png");
    const widthMm = 80;
    const heightMm = (canvas.height * widthMm) / canvas.width;
    const pdf = new jsPDF({ unit: "mm", format: [widthMm, heightMm] });
    pdf.addImage(imgData, "PNG", 0, 0, widthMm, heightMm);
    pdf.save(`Bill-${bill.bill_no.replace(/[/]/g, "-")}.pdf`);
  } catch (e) {
    console.error("PDF error:", e);
    alert("PDF generation failed: " + (e?.message || "unknown error"));
  }
}

// Share bill via Fast2SMS (SMS or WhatsApp)
async function sendViaSms(bill, channel = "sms") {
  const phone = (bill.customer_phone || "").replace(/\D/g, "");
  let target = phone;
  if (!target) {
    target = window.prompt("Customer phone number (10-digit Indian)?", "");
    if (!target) return;
  }
  try {
    const { api } = await import("../lib/api");
    const endpoint = channel === "whatsapp" ? "send-whatsapp" : "send-sms";
    await api.post(`/bills/${bill.bill_id}/${endpoint}`, { phone: target });
    const { toast } = await import("sonner");
    toast.success(`${channel === "whatsapp" ? "WhatsApp" : "SMS"} sent to ${target}`);
  } catch (e) {
    const { toast } = await import("sonner");
    toast.error(e?.response?.data?.detail || "Send failed");
  }
}

// PaymentBody: single or split payment UI (with LP-as-payment support)
function PaymentBody({ netTotal, onPay, loyalty, customerPhone }) {
  const [mode, setMode] = React.useState("single");
  const [singleMethod, setSingleMethod] = React.useState(null);
  const [splitA, setSplitA] = React.useState({ method: "cash", amount: "" });
  const [splitB, setSplitB] = React.useState({ method: "gpay", amount: "" });
  const [lpMode, setLpMode] = React.useState(false); // LP+Cash flow
  const [lpUse, setLpUse] = React.useState(0);

  const hasLP = loyalty && loyalty.balance > 0 && customerPhone && customerPhone.replace(/\D/g, "").length === 10;
  const PM = PAYMENT_METHODS;
  const PM_WITH_LP = hasLP ? [...PM, { id: "loyalty", label: "LP", color: "bg-purple-600" }] : PM;

  // Show LP-combo flow
  if (lpMode) {
    const maxLp = Math.min(loyalty?.balance || 0, netTotal);
    const lpAmt = Math.max(0, Math.min(maxLp, Number(lpUse) || 0));
    const remaining = Math.max(0, netTotal - lpAmt);
    return (
      <div className="space-y-3" data-testid="lp-payment-body">
        <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
          <div className="flex justify-between"><span className="font-bold text-purple-700">Loyalty Balance</span><span className="font-mono font-bold text-purple-700">{loyalty.balance} LP (₹{loyalty.balance})</span></div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs">LP to use:</span>
            <input
              type="number"
              min={0}
              max={maxLp}
              value={lpUse}
              onChange={(e) => setLpUse(e.target.value)}
              className="flex-1 h-9 px-2 border border-border rounded text-right font-mono"
              data-testid="lp-use-input"
            />
            <Button size="sm" variant="outline" onClick={() => setLpUse(maxLp)} data-testid="lp-max-btn">MAX</Button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">1 LP = ₹1 · Max {maxLp}</div>
        </div>
        <div className="text-sm flex justify-between border-t border-border pt-2">
          <span>Remaining to pay</span><span className="font-mono font-bold">₹{Math.round(remaining)}</span>
        </div>
        {remaining === 0 ? (
          <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" disabled={lpAmt === 0} onClick={() => onPay([{ method: "loyalty", amount: lpAmt }])} data-testid="lp-only-pay">
            Pay entirely with LP (₹{lpAmt})
          </Button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {PM.map((m) => (
              <button
                key={m.id}
                disabled={lpAmt === 0}
                onClick={() => onPay([{ method: "loyalty", amount: lpAmt }, { method: m.id, amount: remaining }])}
                data-testid={`lp-plus-${m.id}`}
                className={`${m.color} text-white rounded p-3 text-sm font-bold hover:opacity-90 disabled:opacity-40`}
              >
                LP + {m.label}
                <div className="text-[10px] font-normal opacity-90">₹{lpAmt} + ₹{Math.round(remaining)}</div>
              </button>
            ))}
          </div>
        )}
        <Button variant="ghost" className="w-full" onClick={() => { setLpMode(false); setLpUse(0); }} data-testid="lp-cancel">← Back</Button>
      </div>
    );
  }

  if (mode === "single" && !singleMethod) {
    return (
      <>
        {hasLP && (
          <div className="bg-purple-50 border border-purple-200 rounded p-2 mb-3 text-xs flex justify-between items-center" data-testid="payment-lp-info">
            <span>Customer LP Balance: <b className="text-purple-700 font-mono">{loyalty.balance} LP (₹{loyalty.balance})</b></span>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-[10px]" onClick={() => setLpMode(true)} data-testid="use-lp-btn">Use LP Combo</Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {PM.map((m) => (
            <button
              key={m.id}
              onClick={() => onPay([{ method: m.id, amount: netTotal }])}
              data-testid={`pay-${m.id}`}
              className={`${m.color} text-white rounded-md p-6 font-heading text-lg font-bold hover:opacity-90 transition-opacity`}
            >
              {m.label}
              <div className="text-xs font-normal opacity-90 mt-1">₹{Math.round(netTotal)}</div>
            </button>
          ))}
          {hasLP && (
            <button
              onClick={() => {
                if ((loyalty.balance || 0) < netTotal) {
                  toast.error(`Insufficient LP (${loyalty.balance}). Use split or Combo.`);
                  return;
                }
                onPay([{ method: "loyalty", amount: netTotal }]);
              }}
              data-testid="pay-loyalty"
              className="bg-purple-600 text-white rounded-md p-6 font-heading text-lg font-bold hover:opacity-90 transition-opacity col-span-2"
              style={{ opacity: (loyalty.balance || 0) < netTotal ? 0.5 : 1 }}
            >
              Pay with LP ({Math.round(netTotal)} pts)
              <div className="text-xs font-normal opacity-90 mt-1">Balance: {loyalty.balance} LP</div>
            </button>
          )}
        </div>
        <Button variant="outline" onClick={() => setMode("split")} data-testid="split-payment-btn" className="w-full mt-3 gap-2">
          Split Payment (2 methods)
        </Button>
      </>
    );
  }

  // Split UI
  const a = Number(splitA.amount) || 0;
  const b = Number(splitB.amount) || 0;
  const remaining = +(netTotal - a - b).toFixed(2);
  const ok = remaining === 0 && a > 0 && b > 0 && splitA.method !== splitB.method;
  return (
    <div className="space-y-3" data-testid="split-payment-body">
      <div className="text-xs text-muted-foreground">Split bill across 2 payment methods. Sum must equal ₹{Math.round(netTotal)}.</div>
      {[
        ["1st", splitA, setSplitA, "split-a"],
        ["2nd", splitB, setSplitB, "split-b"],
      ].map(([label, s, setS, tid]) => (
        <div key={label} className="flex gap-2 items-center">
          <span className="w-10 text-xs text-muted-foreground">{label}</span>
          <select className="border border-border rounded h-9 px-2 text-sm" value={s.method} onChange={(e)=>setS({...s, method: e.target.value})} data-testid={`${tid}-method`}>
            {PM_WITH_LP.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <input type="number" placeholder="Amount" className="flex-1 h-9 px-2 border border-border rounded text-right font-mono" value={s.amount} onChange={(e)=>setS({...s, amount: e.target.value})} data-testid={`${tid}-amount`}/>
        </div>
      ))}
      <div className={`flex justify-between text-sm font-mono p-2 rounded ${remaining === 0 ? "bg-moss/20 text-moss" : "bg-destructive/10 text-destructive"}`}>
        <span>Remaining</span>
        <span className="font-bold">₹{Math.round(remaining)}</span>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setMode("single")} className="flex-1">Back</Button>
        <Button disabled={!ok} onClick={() => onPay([{method: splitA.method, amount: a}, {method: splitB.method, amount: b}])} data-testid="split-checkout-btn" className="flex-1 bg-terracotta hover:bg-terracotta-hover text-white">
          Confirm Split
        </Button>
      </div>
    </div>
  );
}

// Print via new window for reliable thermal printing
function printReceipt(bill, shop) {
  const container = document.getElementById("receipt-preview-container");
  if (!container) return;
  // Convert all canvases to img dataURLs (canvas pixels don't survive innerHTML clone)
  const clone = container.cloneNode(true);
  const liveCanvases = container.querySelectorAll("canvas");
  const cloneCanvases = clone.querySelectorAll("canvas");
  liveCanvases.forEach((c, i) => {
    try {
      const img = document.createElement("img");
      img.src = c.toDataURL("image/png");
      img.style.width = c.style.width || c.width + "px";
      img.style.height = c.style.height || c.height + "px";
      cloneCanvases[i].replaceWith(img);
    } catch (e) { /* ignore */ }
  });
  const html = clone.innerHTML;
  const w = window.open("", "_blank", "width=400,height=700");
  if (!w) {
    alert("Please allow popups for this site to print bills.");
    return;
  }
  w.document.write(`<!doctype html><html><head>
<title>Bill ${bill.bill_no}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600;700;800&family=Manrope:wght@600;700;800&display=swap" rel="stylesheet">
<style>
  @page { size: 80mm auto; margin: 0; }
  *,*::before,*::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: white; font-family: 'IBM Plex Mono', monospace; color: black; font-size: 14px; font-weight: 700; line-height: 1.3; }
  body { padding: 0; width: 80mm; }
  .border { border: 1.5px solid black; border-radius: 4px; padding: 6px; }
  .border-2 { border: 1.5px solid black; border-radius: 4px; padding: 6px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 1px 0; }
  .text-center { text-align: center; }
  .text-right { text-align: right; }
  .text-left { text-align: left; }
  b, th, .font-bold { font-weight: 800 !important; }
  .font-heading { font-family: 'Manrope', sans-serif; font-weight: 800; letter-spacing: -0.01em; }
  .text-2xl { font-size: 22px; font-weight: 800; }
  .text-lg { font-size: 16px; }
  .text-xs { font-size: 12px; font-weight: 700; }
  .text-\\[10px\\] { font-size: 11px; font-weight: 700; }
  .text-\\[11px\\] { font-size: 12px; font-weight: 700; }
  .dashed-divider { border-top: 1.8px dashed #000; margin: 4px 0; }
  .grid { display: grid; }
  .grid-cols-2 { grid-template-columns: 1fr 1fr; gap: 6px; }
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .justify-center { justify-content: center; }
  .gap-2 { gap: 6px; }
  .my-2 { margin: 4px 0; }
  .mt-1 { margin-top: 2px; }
  .mt-2 { margin-top: 4px; }
  .mb-1 { margin-bottom: 2px; }
  .py-1 { padding: 1px 0; }
  .px-1 { padding: 0 4px; }
  .pt-1 { padding-top: 2px; }
  .border-t { border-top: 1.2px solid #000; }
  .border-b { border-bottom: 1.2px solid #000; }
  .border-dashed { border-style: dashed; border-color: #000; }
  .italic { font-style: italic; }
  .uppercase { text-transform: uppercase; }
  .tracking-wide { letter-spacing: 0.04em; }
  .printable { width: 80mm !important; padding: 0 !important; margin: 0 !important; border: none !important; }
  .receipt-paper { padding: 0 !important; }
  img, svg, canvas { display: block; max-width: 100%; }
</style>
</head><body>${html}</body></html>`);
  w.document.close();
  // Wait for resources to load before printing
  setTimeout(() => {
    try { w.focus(); w.print(); } catch (e) {}
    setTimeout(() => { try { w.close(); } catch (e) {} }, 1500);
  }, 600);
}
